import { Asset, FolderContent } from '@/api/generatedApi';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { Thumbnail } from '@/components/Thumbnail';
import { AddToCompareButton } from '@/components/CompareCart/AddToCompareButton';
import { FindMatchesButton } from '@/components/Matches/FindMatchesButton';
import { DownloadAssetButton } from '@/components/AssetActionRow/DownloadAssetButton';
import { DeleteAssetButton } from '@/components/AssetActionRow/DeleteAssetButton';
import { getAssetName } from '@/utils/assets';
import { formatMatchPercentage } from '@/utils/format';
import { MediaArea, StyledCard, StyledCardContent, InlineLink } from '@/components/CardGrid/cardStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { getFileExtension } from '@physna/supported-files';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { ReportBreadcrumbToolbar } from './ReportBreadcrumbToolbar';

const CARD_WIDTH = 240;
const CARD_HEIGHT = 280;

const THUMBNAIL_SIZE = 160;

const actionRowSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 0.5,
  '& .MuiIconButton-root': { padding: '6px' },
  '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
};

const MatchPill = ({ matchPercentage }: { matchPercentage: number }) => (
  <Chip
    label={`${formatMatchPercentage(matchPercentage)}%`}
    size='small'
    sx={{
      position: 'absolute',
      top: 8,
      right: 8,
      fontWeight: 600,
      zIndex: 1,
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
    }}
  />
);

const MatchAssetCard = ({
  asset,
  matchPercentage,
  primaryAssetId,
  onContextMenu,
  onSetAsReference,
}: {
  asset: Asset;
  matchPercentage: number;
  primaryAssetId: string;
  onContextMenu?: (event: React.MouseEvent) => void;
  onSetAsReference?: () => void;
}) => {
  const { getPath } = useTenants();
  const extension = getFileExtension(asset.path);
  const assetPath = getPath(`asset/${asset.id}`);

  return (
    <Box
      onContextMenu={onContextMenu}
      sx={{ position: 'relative', width: CARD_WIDTH, height: CARD_HEIGHT, flexShrink: 0 }}
    >
      <StyledCard sx={{ cursor: 'default' }}>
        <MediaArea>
          <MatchPill matchPercentage={matchPercentage} />
          <Thumbnail asset={asset} size={THUMBNAIL_SIZE} />
        </MediaArea>
        <StyledCardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <InlineLink to={assetPath} draggable={false} sx={{ display: 'block', overflow: 'hidden', minWidth: 0 }}>
              <Typography
                variant='body2'
                component='span'
                sx={{
                  fontWeight: 600,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getAssetName(asset)}
              </Typography>
            </InlineLink>
            {extension && <Chip label={extension} size='small' variant='outlined' sx={{ flexShrink: 0 }} />}
          </Box>
        </StyledCardContent>
        <Box sx={actionRowSx}>
          {onSetAsReference && (
            <Tooltip title='Set as reference asset'>
              <IconButton size='small' onClick={onSetAsReference}>
                <SwapHorizIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <FindMatchesButton asset={asset} variant='icon' />
          <AddToCompareButton asset={asset} variant='icon' quickCompare={primaryAssetId} />
          <DownloadAssetButton asset={asset} variant='icon' />
          <DeleteAssetButton asset={asset} variant='icon' />
        </Box>
      </StyledCard>
    </Box>
  );
};

export const ReportMatchListView = ({
  assets,
  selectedAssetId,
  groupName,
  isLoading,
  toolbarActions,
  onCardContextMenu,
  onSetAsReference,
}: {
  assets: ReportGroupAsset[];
  selectedAssetId: string | undefined;
  groupName: string;
  isLoading: boolean;
  toolbarActions?: React.ReactNode;
  onCardContextMenu?: (asset: FolderContent, event: React.MouseEvent) => void;
  onSetAsReference?: (assetId: string) => void;
}) => {
  const groupAssetIds = new Set(assets.map(a => a.id));
  const effectiveSelectedId = selectedAssetId ?? assets[0]?.id;
  const selectedAsset = assets.find(a => a.id === effectiveSelectedId);

  const matches = selectedAsset?.matches ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <ReportBreadcrumbToolbar
        assets={assets}
        selectedAssetId={selectedAssetId}
        groupName={groupName}
        toolbarActions={toolbarActions}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && matches.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, height: '100%' }}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              No matches for this asset.
            </Typography>
          </Box>
        )}
        {!isLoading && matches.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              padding: 2,
            }}
          >
            {matches.map(match => (
              <MatchAssetCard
                key={match.asset.id}
                asset={match.asset}
                matchPercentage={match.matchPercentage}
                primaryAssetId={selectedAsset!.id}
                onSetAsReference={
                  onSetAsReference && groupAssetIds.has(match.asset.id)
                    ? () => onSetAsReference(match.asset.id)
                    : undefined
                }
                onContextMenu={
                  onCardContextMenu
                    ? event => {
                        event.preventDefault();
                        onCardContextMenu({ ...match.asset, contentType: 'asset' as const }, event);
                      }
                    : undefined
                }
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
